import { useState } from "react"
import { AlertCircle, Eye, EyeOff } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

// The auth screens' text field: the design's 10px-radius, line-2-bordered
// input with an ink focus ring (design/spec/auth/auth.html `.inp`), applied
// over the DS Input so provenance stays with components/ui.
export const FIELD_CLASS =
  "h-auto rounded-[10px] border-line-2 bg-card px-[14px] py-3 text-[14px] shadow-none placeholder:text-ink-3 focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-foreground/[0.07] md:text-[14px] dark:bg-card"

const LABEL_CLASS = "text-[12.5px] font-bold text-foreground"

// Rails forgery protection reads the session token from the form body. The
// auth forms post as ordinary browser forms (a full navigation, so Devise's
// redirects reach every user type), so the token travels as a hidden field.
export function CsrfField() {
  const token =
    typeof document === "undefined"
      ? ""
      : document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

  return <input type="hidden" name="authenticity_token" value={token} />
}

export function FieldError({ children }) {
  return (
    <p className="flex items-center gap-1.5 text-[12px] font-semibold text-primary">
      <AlertCircle className="size-3.5 shrink-0" strokeWidth={2.2} />
      {children}
    </p>
  )
}

export function TextField({ id, label, hint, error, className, ...inputProps }) {
  return (
    <div className="flex flex-col gap-[7px]">
      <Label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </Label>
      <Input
        id={id}
        aria-invalid={error ? true : undefined}
        className={cn(FIELD_CLASS, className)}
        {...inputProps}
      />
      {error ? <FieldError>{error}</FieldError> : hint ? <p className="text-[12px] leading-[1.45] text-ink-3">{hint}</p> : null}
    </div>
  )
}

// Password with a show/hide toggle. `extra` sits on the label row (the sign in
// screen's "Forgot password?" link); `error` replaces `hint` below the field.
export function PasswordField({ id, label, extra, hint, error, className, ...inputProps }) {
  const [shown, setShown] = useState(false)

  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex items-center justify-between gap-2.5">
        <Label htmlFor={id} className={LABEL_CLASS}>
          {label}
        </Label>
        {extra}
      </div>
      <div className="relative flex items-center">
        <Input
          id={id}
          type={shown ? "text" : "password"}
          aria-invalid={error ? true : undefined}
          className={cn(FIELD_CLASS, "pr-11", className)}
          {...inputProps}
        />
        <button
          type="button"
          onClick={() => setShown((value) => !value)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          className="absolute right-1.5 flex size-8 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-secondary hover:text-foreground"
        >
          {shown ? (
            <EyeOff className="size-[17px]" strokeWidth={1.9} />
          ) : (
            <Eye className="size-[17px]" strokeWidth={1.9} />
          )}
        </button>
      </div>
      {error ? <FieldError>{error}</FieldError> : hint ? <p className="text-[12px] leading-[1.45] text-ink-3">{hint}</p> : null}
    </div>
  )
}
